import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ACTIVE_GAME_CATALOG, getGameById } from "../../data/gameCatalog";
import { SeoMeta, SiteFooter, SiteNavbar, dataUrl, formatOs, statusLabel } from "../../shared/site";
import CategoryPage from "./CategoryPage";

const legacyProductPages = require.context(
  "./",
  false,
  /^\.\/(?!(?:\d+|CategoryPage|ProductPage|ProductPageShell|ProductRoute|Vierge navbar widget footer)\.js$).+\.js$/,
  "lazy"
);

function LoadingProductPage() {
  return (
    <div className="min-h-screen bg-transparent px-4 pt-28 text-white font-jersey">
      <div className="themed-skeleton themed-skeleton-product mx-auto grid max-w-6xl gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="themed-skeleton-console lg:col-span-2">
          <span className="themed-skeleton-led" />
          <span>loading product terminal</span>
          <strong>ROUTE</strong>
        </div>
        <div className="themed-skeleton-card rounded-lg border border-purple-400/20 bg-gray-950/55 p-5">
          <span className="themed-skeleton-node" />
          <span className="skeleton-shimmer block aspect-video rounded-lg" />
          <div className="mt-5 space-y-3">
            <span className="skeleton-shimmer block h-8 w-2/3 rounded" />
            <span className="skeleton-shimmer block h-4 w-full rounded" />
            <span className="skeleton-shimmer block h-4 w-4/5 rounded" />
          </div>
        </div>
        <div className="themed-skeleton-card rounded-lg border border-purple-400/20 bg-gray-950/55 p-5">
          <span className="themed-skeleton-node" />
          <span className="skeleton-shimmer block h-10 w-3/4 rounded" />
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }, (_, index) => (
              <span key={`product-route-skeleton-${index}`} className="skeleton-shimmer block h-16 rounded-lg" />
            ))}
          </div>
          <span className="skeleton-shimmer mt-6 block h-12 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function ProductNotFound({ slug }) {
  return (
    <div className="min-h-screen bg-transparent text-white font-jersey flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Product not found</h1>
        <p className="text-xl text-gray-300">
          No real product page exists for /games/{slug}.
        </p>
      </div>
    </div>
  );
}

function ProductRouteSeo({ slug }) {
  const [productResult, setProductResult] = useState(null);

  useEffect(() => {
    let active = true;
    const productPath = `/games/${slug}`;

    Promise.all(
      ACTIVE_GAME_CATALOG.map(async (game) => {
        try {
          const response = await fetch(dataUrl(game.dataFile), { cache: "no-store" });
          if (!response.ok) return null;

          const rows = await response.json();
          if (!Array.isArray(rows)) return null;

          const product = rows.find((item) => String(item.link || "").toLowerCase() === productPath);
          return product ? { game, product } : null;
        } catch {
          return null;
        }
      })
    ).then((results) => {
      if (active) setProductResult(results.find(Boolean) || null);
    });

    return () => {
      active = false;
    };
  }, [slug]);

  if (!productResult) {
    return (
      <SeoMeta
        title={`${slug} product`}
        description="Product details, live status, prices and compatibility on HackSeller."
        canonicalPath={`/games/${slug}`}
      />
    );
  }

  const { game, product } = productResult;
  const productName = String(product.name || "").trim();
  const gameName = String(game.name || "").trim();
  const title = `${productName} ${gameName} Cheat`;
  const description = `${productName} for ${gameName}: live ${statusLabel(product.status).toLowerCase()} status, price ${product.price || "available on page"}, Windows compatibility ${formatOs(product.os)} and product details.`;

  return (
    <SeoMeta
      title={title}
      description={description}
      image={product.logo || game.image}
      imageAlt={`${productName} ${gameName} product image`}
      canonicalPath={product.link || `/games/${slug}`}
    />
  );
}

function ProductPreviewMode() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("preview") !== "1") return undefined;

    document.body.dataset.productPreview = "true";

    const stopPreviewNavigation = (event) => {
      const link = event.target?.closest?.("a");
      if (!link) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    document.addEventListener("click", stopPreviewNavigation, true);

    return () => {
      document.removeEventListener("click", stopPreviewNavigation, true);
      delete document.body.dataset.productPreview;
    };
  }, []);

  return null;
}

function LegacyProductNavbarMode() {
  useEffect(() => {
    document.body.dataset.legacyProductNavbar = "true";

    return () => {
      delete document.body.dataset.legacyProductNavbar;
    };
  }, []);

  return null;
}

export default function ProductRoute() {
  const { gameId } = useParams();
  const slug = String(gameId || "").toLowerCase();
  const category = getGameById(slug);
  const legacyPageKey = `./${slug}.js`;

  const LegacyProductPage = useMemo(() => {
    if (!legacyProductPages.keys().includes(legacyPageKey)) {
      return null;
    }

    return React.lazy(() => legacyProductPages(legacyPageKey));
  }, [legacyPageKey]);

  if (category) {
    return <CategoryPage game={category} />;
  }

  if (LegacyProductPage) {
    return (
      <Suspense fallback={<LoadingProductPage />}>
        <LegacyProductNavbarMode />
        <ProductPreviewMode />
        <SiteNavbar />
        <ProductRouteSeo slug={slug} />
        <LegacyProductPage />
        <SiteFooter />
      </Suspense>
    );
  }

  return <ProductNotFound slug={slug} />;
}
